// Jul-2026 (Bugfix „Cloudflare-Timeout bei Scan-Erfassung") — lange KI-Scans
// laufen als Hintergrund-Job: Upload antwortet sofort, danach Status-Polling.
import api from "./api";

// Jul-2026 (Bugfix „Scan fehlgeschlagen bei Mehrseiten-Scans") — große Dateien
// in KLEINEN Teilen hochladen (Proxy-/Ingress-Body-Limits!), dann Job starten.
// 768 KB, da manche Proxys schon bei ~1 MB Request-Body abriegeln.
const CHUNK_SIZE = 768 * 1024;

async function postChunk(fd, attempt = 0) {
    try {
        await api.post("/scan-jobs/upload-chunk", fd, {
            headers: { "Content-Type": "multipart/form-data" }, timeout: 60000,
        });
    } catch (e) {
        if (attempt < 2) {
            await new Promise((r) => setTimeout(r, 1200 * (attempt + 1)));
            return postChunk(fd, attempt + 1);
        }
        const st = e.response?.status;
        throw new Error(st
            ? `Upload-Teil abgelehnt (HTTP ${st}) — bitte erneut versuchen`
            : "Netzwerkfehler beim Upload — bitte Verbindung prüfen und erneut versuchen");
    }
    return undefined;
}

async function uploadInChunks(kind, file, onPhase, params) {
    const spec = await uploadFileChunks(file, onPhase);
    if (params) spec.params = params;
    try {
        const { data } = await api.post(`/scan-jobs/${kind}/start`, spec, { timeout: 60000 });
        return data;
    } catch (e) {
        throw new Error(e.response?.data?.detail
            || `Verarbeitung konnte nicht gestartet werden (HTTP ${e.response?.status || "Netzwerk"})`);
    }
}

async function uploadFileChunks(file, onPhase) {
    const uploadId = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`);
    const total = Math.ceil(file.size / CHUNK_SIZE);
    // Aug-2026 (User-Report „Upload dauert zu lange"): Chunks PARALLEL hochladen
    // (4 gleichzeitig) statt nacheinander — 3-4× schnellerer Upload.
    const indices = Array.from({ length: total }, (_, i) => i);
    let done = 0;
    const worker = async () => {
        while (indices.length) {
            const i = indices.shift();
            const fd = new FormData();
            fd.append("upload_id", uploadId);
            fd.append("index", String(i));
            fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE), "chunk.bin");
            // eslint-disable-next-line no-await-in-loop
            await postChunk(fd);
            done += 1;
            onPhase?.("upload", { done, total });
        }
    };
    await Promise.all(Array.from({ length: Math.min(4, total) }, worker));
    return {
        upload_id: uploadId, total_chunks: total,
        filename: file.name || "scan", mime: file.type || "application/octet-stream",
    };
}

async function directUpload(kind, file, params) {
    const form = new FormData();
    form.append("file", file, file.name || "scan");
    if (params) form.append("params", JSON.stringify(params));
    try {
        return (await api.post(`/scan-jobs/${kind}`, form, {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 120000,
        })).data;
    } catch (e) {
        throw new Error(e.response?.data?.detail
            || `Upload fehlgeschlagen (HTTP ${e.response?.status || "Netzwerk"}) — bitte erneut versuchen`);
    }
}

export async function runScanJob(kind, file, { pollMs = 1500, timeoutMs = 600000, onStatus, onPhase, params } = {}) {
    onPhase?.("upload");
    // Jul-2026 (Prod-Bugfix Cloudflare 520) — zweigleisig: bevorzugte Methode
    // je nach Dateigröße, bei Proxy-Fehlern automatisch die andere probieren.
    const preferChunks = file.size > CHUNK_SIZE;
    let data;
    try {
        data = preferChunks
            ? await uploadInChunks(kind, file, onPhase, params)
            : await directUpload(kind, file, params);
    } catch (e1) {
        try {
            data = preferChunks
                ? await directUpload(kind, file, params)
                : await uploadInChunks(kind, file, onPhase, params);
        } catch {
            throw e1;
        }
    }
    onPhase?.("processing");
    return pollJob(data.job_id, { pollMs, timeoutMs, onStatus });
}

async function pollJob(jobId, { pollMs = 2500, timeoutMs = 300000, onStatus } = {}) {
    const deadline = Date.now() + timeoutMs;
    while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, pollMs));
        let job;
        try {
            job = (await api.get(`/scan-jobs/${jobId}`)).data;
        } catch {
            continue; // kurzer Netz-Schluckauf → weiter pollen
        }
        onStatus?.(job);
        if (job.status === "done") return job.result;
        if (job.status === "error") throw new Error(job.error || "Verarbeitung fehlgeschlagen");
    }
    throw new Error("Zeitüberschreitung bei der Verarbeitung — bitte erneut versuchen");
}

// Jul-2026 (502-Fix Multi-Doc) — ZB1/COC/Serviceheft-Batch als Hintergrund-Job:
// alle Dateien in Chunks hochladen (parallel), Batch-Job starten, Status pollen.
export async function runDocumentsScanJob(files, { pollMs = 2500, timeoutMs = 600000, onStatus, onPhase } = {}) {
    onPhase?.("upload");
    const specs = await Promise.all(files.map((f) => uploadFileChunks(f)));
    let data;
    try {
        data = (await api.post("/scan-jobs/documents/start-batch", { files: specs }, { timeout: 60000 })).data;
    } catch (e) {
        throw new Error(e.response?.data?.detail
            || `Verarbeitung konnte nicht gestartet werden (HTTP ${e.response?.status || "Netzwerk"})`);
    }
    onPhase?.("processing");
    return pollJob(data.job_id, { pollMs, timeoutMs, onStatus });
}
